import { ErrorBoundary, type FallbackProps } from "react-error-boundary";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import ResponsiveImage from "../responsive-image";

function isChunkError(error: unknown): boolean {
	return (
		error instanceof Error &&
		/Loading chunk [\d]+ failed|ChunkLoadError|Failed to fetch dynamically imported module/i.test(
			error.message,
		)
	);
}

const ChunkErrorView = () => (
	<div className="flex flex-col items-center justify-center">
		<div className="relative w-1/2 mb-6">
			<ResponsiveImage
				baseName={"spilt-coffee"} // Use a base name for the error image
				alt={"Split coffee illustration - Error"}
				className={"object-contain rounded-lg"}
			/>
		</div>
		<div className="max-w-md w-full mx-auto flex flex-col items-center text-center">
			<h1 className="text-2xl md:text-3xl font-bold mb-2">
				Oops! Something went wrong.
			</h1>
			<p className="text-muted-foreground mb-6">
				The app you're running is out-of-date. Please try refreshing the page to
				resolve the issue.
			</p>
			<Button
				onClick={() => window.location.reload()}
				className="flex items-center gap-2"
				size="lg"
			>
				<RefreshCw className="h-4 w-4" />
				Reload
			</Button>
			<p className="text-sm text-muted-foreground mt-6">
				If the problem persists, please contact our support team.
			</p>
		</div>
	</div>
);

const GenericErrorView = () => (
	<div className="flex flex-col h-svh items-center justify-center text-center px-4">
		<h1 className="text-2xl md:text-3xl font-bold mb-2">
			Something went wrong.
		</h1>
		<p className="mb-6">
			An unexpected error occurred. Please try again or contact support at
			help@coffitivity.com.
		</p>
		<Button
			onClick={() => window.location.reload()}
			className="flex items-center gap-2"
			size="lg"
		>
			Try Again
		</Button>
	</div>
);

function ErrorFallback({ error }: FallbackProps) {
	return isChunkError(error) ? <ChunkErrorView /> : <GenericErrorView />;
}

export function AppErrorBoundary({
	children,
	className,
}: { children: React.ReactNode; className?: string }) {
	return (
		<ErrorBoundary FallbackComponent={ErrorFallback}>
			{className ? <div className={className}>{children}</div> : children}
		</ErrorBoundary>
	);
}
